import {
  buildActivationCheckoutLocationState,
  getActivationFlowData,
  saveActivationFlowData,
} from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import {
  frequencyKeyFromMonths,
  normalizePrimaryDataTierKey,
  preloadedSimServiceFrequencyKeyToMonths,
} from "./preloaded-sim-plan-mappers";
import {
  getSecurityPlanId,
  resolveIncludedSecurityPlanIdFromSimData,
  resolveIncludedSecurityPlanTierFromSimData,
} from "./included-security-plan-utils";
import { persistSimCardUpsellKeepIncludedAllocation } from "./sim-card-upsell-plan-persistence";

export interface PreloadedSimUpsellStepSkips {
  skipChooseServicePlan: boolean;
  skipSecurityPlan: boolean;
}

/**
 * Derives which PSD activation upsell steps are skipped from SIM inventory `simData`.
 * Rules (product):
 * - Unlimited + 12 months → skip service-plan step (no higher tier to sell).
 * - Included security is Family Complete → skip security upgrade step.
 */
export function resolvePreloadedSimUpsellStepSkips(
  simData: unknown,
): PreloadedSimUpsellStepSkips {
  if (!simData || typeof simData !== "object") {
    return { skipChooseServicePlan: false, skipSecurityPlan: false };
  }
  const data = simData as Record<string, unknown>;
  const included = data.planIncluded;
  const includedPlan =
    included && typeof included === "object"
      ? (included as Record<string, unknown>)
      : null;
  const includedTierKey = normalizePrimaryDataTierKey(includedPlan?.name);
  const includedFrequencyKey = frequencyKeyFromMonths(includedPlan?.frequency);
  const skipChooseServicePlan =
    includedTierKey === "unlimited" && includedFrequencyKey === "twelveMonths";

  const securityTier = resolveIncludedSecurityPlanTierFromSimData(simData);
  const skipSecurityPlan = securityTier === "completeFamily";

  return { skipChooseServicePlan, skipSecurityPlan };
}

/** Same request shape as {@link SecurityPlanUpgrade} / RTK `getMobileSimInventorySecurityPlans`. */
function unwrapMobileSimInventorySecurityPlansList(sec: unknown): unknown[] {
  const root = sec && typeof sec === "object" ? (sec as Record<string, unknown>) : null;
  const inner =
    root?.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : root;
  const list = inner?.securityPlans;
  return Array.isArray(list) ? list : [];
}

/**
 * After `getMobileSimInventorySecurityPlans`, pick the Complete Family plan id
 * (same matching rules as {@link SecurityPlanUpgrade}).
 */
export function pickCompleteFamilySecurityPlanIdFromInventoryResponse(
  sec: unknown,
): string | undefined {
  const securityPlans = unwrapMobileSimInventorySecurityPlansList(sec);
  const completeFamilyPlan = securityPlans.find((plan: unknown) => {
    const p = plan as { securityPlanName?: unknown; planName?: unknown };
    return String(p?.securityPlanName || p?.planName || "")
      .toLowerCase()
      .includes("family");
  });
  return getSecurityPlanId(completeFamilyPlan);
}

/** Request params when aligning Family Complete security billing to a chosen service cadence. */
export function buildPreloadedSimFamilyCompleteSecurityPlansRequestParams(
  serviceFrequencyKey: string,
): { frequency: number; securityPlanName: string } {
  const months = preloadedSimServiceFrequencyKeyToMonths(serviceFrequencyKey);
  const frequency =
    months != null && [1, 3, 6, 12].includes(months) ? months : 1;
  return { frequency, securityPlanName: "Aura Family Complete" };
}

/**
 * Step 3 is skipped (Family Complete), but checkout still needs the security catalog
 * when the customer **changed billing cadence** vs the SIM — same tier, new frequency.
 */
export function preloadedSimFamilyCompleteNeedsSecurityPlanApiForCheckout(
  simData: unknown,
  selectedServiceFrequencyKey: string | undefined,
): boolean {
  const { skipSecurityPlan } = resolvePreloadedSimUpsellStepSkips(simData);
  if (!skipSecurityPlan) return false;
  const includedFk = frequencyKeyFromMonths(
    simData && typeof simData === "object"
      ? ((simData as Record<string, unknown>).planIncluded as Record<string, unknown> | undefined)
          ?.frequency
      : undefined,
  );
  const sel = String(selectedServiceFrequencyKey || "").trim();
  const selectedFk = sel || includedFk;
  return selectedFk !== includedFk;
}

export function buildPreloadedSimSecurityPlansRequestParamsFromSimData(
  simData: unknown,
): { frequency: number; securityPlanName?: string } {
  const includedPlan =
    simData && typeof simData === "object"
      ? ((simData as Record<string, unknown>).planIncluded as
          | Record<string, unknown>
          | undefined)
      : undefined;
  const planFrequencyFromSim = Number.parseInt(
    String(includedPlan?.frequency ?? "1"),
    10,
  );
  const frequency =
    Number.isFinite(planFrequencyFromSim) && [1, 3, 6, 12].includes(planFrequencyFromSim)
      ? planFrequencyFromSim
      : 1;
  const tier = resolveIncludedSecurityPlanTierFromSimData(simData);
  return {
    frequency,
    ...(tier === "completeFamily" ? { securityPlanName: "Aura Family Complete" } : {}),
  };
}

export function getPreloadedSimVisibleUpsellPathnames(
  skips: PreloadedSimUpsellStepSkips,
): string[] {
  const paths = ["/activation/preloaded-sim/device-info"];
  if (!skips.skipChooseServicePlan) {
    paths.push("/activation/preloaded-sim/choose-service-plan");
  }
  if (!skips.skipSecurityPlan) {
    paths.push("/activation/preloaded-sim/security-plan-upgrade");
  }
  return paths;
}

export function getPreloadedSimProgressStepIndex(
  pathname: string,
  skips: PreloadedSimUpsellStepSkips,
): number {
  const visible = getPreloadedSimVisibleUpsellPathnames(skips);
  const idx = visible.indexOf(pathname);
  return idx >= 0 ? idx : 0;
}

/**
 * After device congratulations: next upsell route, or `null` when both upsells
 * are skipped (caller should go straight to checkout).
 */
export function getPreloadedSimPostDeviceUpsellPath(
  skips: PreloadedSimUpsellStepSkips,
): string | null {
  if (skips.skipChooseServicePlan && skips.skipSecurityPlan) {
    return null;
  }
  if (!skips.skipChooseServicePlan) {
    return "/activation/preloaded-sim/choose-service-plan";
  }
  return "/activation/preloaded-sim/security-plan-upgrade";
}

export function commitPreloadedSimIncludedSecuritySelection(): void {
  const activationData = getActivationFlowData();
  const includedPlanId = resolveIncludedSecurityPlanIdFromSimData(
    activationData?.simData,
  );
  saveActivationFlowData({
    optForSecurityPlan: true,
    securityPlanId: includedPlanId,
  });
}

/** Persist Family Complete security after resolving plan id for the checkout cadence. */
export function commitPreloadedSimFamilyCompleteSecurityPlanId(
  planId: string | undefined,
): void {
  const normalized = String(planId || "").trim();
  saveActivationFlowData({
    optForSecurityPlan: true,
    ...(normalized ? { securityPlanId: normalized } : {}),
  });
}

/** Navigate targets after checkout — last upsell step the user would have seen. */
export function getPreloadedSimCheckoutBackTargetPathname(): string {
  const simData = getActivationFlowData()?.simData;
  const skips = resolvePreloadedSimUpsellStepSkips(simData);
  const visible = getPreloadedSimVisibleUpsellPathnames(skips);
  if (visible.length <= 1) {
    return "/activation/preloaded-sim/device-info";
  }
  return visible[visible.length - 1]!;
}

/**
 * When activation checkout is missing plan fields, send user to the first step
 * that can restore them (device if service step is skipped).
 */
export function getPreloadedSimMissingPlanFieldsRedirectPath(): string {
  const simData = getActivationFlowData()?.simData;
  const skips = resolvePreloadedSimUpsellStepSkips(simData);
  if (skips.skipChooseServicePlan) {
    return "/activation/preloaded-sim/device-info";
  }
  return "/activation/preloaded-sim/choose-service-plan";
}

export function getPreloadedSimCheckoutNavigateState() {
  return {
    state: buildActivationCheckoutLocationState(),
  };
}

/**
 * Skip both upsell steps: persist included service + security, then caller navigates to checkout.
 */
export function preparePreloadedSimSkipAllUpsellsForCheckout(): void {
  persistSimCardUpsellKeepIncludedAllocation();
  commitPreloadedSimIncludedSecuritySelection();
}
